import {
  CalendarOutlined,
  FilterOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  DownOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  InputNumber,
  Row,
  Select,
  Space,
  Typography,
  Radio,
} from "antd";
import { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { PrintByBookings } from "./print-by-booking";
import { PrintByInventory } from "./print-by-inventory";
import { ClientStatus, InventoryType } from "~/utils/constants";
import { useGetLocations } from "~/services/common";
import { useGetClients } from "~/services/client/client.queries";
import { TInventory } from "~/types/inventory";
import { PrintLabels } from "./print-label";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

export const PrintLabelsComponent: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or defaults
  const [printMode, setPrintMode] = useState<"inventory" | "bookings">(
    searchParams.get("mode") === "bookings" ? "bookings" : "inventory"
  );
  const [selectedDate, setSelectedDate] = useState<string>(
    searchParams.get("date") || new Date().toISOString().split("T")[0]
  );
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [compactView, setCompactView] = useState<boolean>(
    searchParams.get("compact") === "true"
  );
  const [selectedVessel, setSelectedVessel] = useState<string>(
    searchParams.get("vessel") || "all"
  );
  const [selectedClient, setSelectedClient] = useState<string>(
    searchParams.get("client") || "all"
  );
  const [selectedLocation, setSelectedLocation] = useState<string>(
    searchParams.get("location") || "all"
  );
  const [itemType, setItemType] = useState<InventoryType | "all">(
    (searchParams.get("itemType") as InventoryType) || "all"
  );
  const { data: locations, isLoading: locationsLoading } = useGetLocations();
  const { data: clients, isLoading: clientsLoading } = useGetClients({
    params: { status: ClientStatus.ACTIVE, sortByName: "ASC", getAll: true },
  });
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [allSelectedInventories, setAllSelectedInventories] = useState<
    TInventory[]
  >([]);
  const [copyCount, setCopyCount] = useState<number>(1);
  const [printOrder, setPrintOrder] = useState<string>(
    searchParams.get("order") || "default"
  );

  // Update URL when parameters change
  useEffect(() => {
    setSearchParams(
      (params) => {
        params.set("mode", printMode);

        // Only add params that are not default values
        if (selectedDate) params.set("date", selectedDate);
        if (compactView) params.set("compact", String(compactView));
        if (selectedVessel !== "all") params.set("vessel", selectedVessel);
        if (selectedClient !== "all") params.set("client", selectedClient);
        if (selectedLocation !== "all")
          params.set("location", selectedLocation);
        if (itemType !== "all") params.set("itemType", itemType);
        if (printOrder !== "default") params.set("order", printOrder);

        return params;
      },
      { replace: true }
    );
  }, [
    printMode,
    selectedDate,
    compactView,
    selectedVessel,
    selectedClient,
    selectedLocation,
    itemType,
    printOrder,
    setSearchParams,
  ]);

  // Handle state changes with URL updates
  const handlePrintModeChange = (mode: "inventory" | "bookings") => {
    setPrintMode(mode);
  };

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    setSelectedDate(date?.format("YYYY-MM-DD") || "");
  };

  const handleClientChange = (value: string) => {
    setSelectedClient(value);
  };

  const handleLocationChange = (value: string) => {
    setSelectedLocation(value);
  };

  const handleItemTypeChange = (value: InventoryType | "all") => {
    setItemType(value);
  };

  const handleVesselChange = (value: string) => {
    setSelectedVessel(value);
  };

  const handleCompactViewChange = (e: any) => {
    setCompactView(e.target.value);
  };

  const handlePrintOrderChange = (value: string) => {
    setPrintOrder(value);
  };

  const getSortedInventories = useCallback(
    (inventories: TInventory[], order: string) => {
      if (!inventories.length) return [];

      switch (order) {
        case "client":
          return [...inventories].sort((a, b) => {
            return (a.client?.companyName || "").localeCompare(
              b.client?.companyName || ""
            );
          });
        case "location":
          return [...inventories].sort((a, b) => {
            return (a.location?.name || "").localeCompare(
              b.location?.name || ""
            );
          });
        case "type":
          return [...inventories].sort((a, b) => {
            return (a.itemType || "").localeCompare(b.itemType || "");
          });
        case "title":
          return [...inventories].sort((a, b) => {
            return (a.title || "").localeCompare(b.title || "");
          });
        case "default":
          return [...inventories].sort((a, b) => {
            return (a.inventoryRef || "").localeCompare(b.inventoryRef || "");
          });
        default:
          return inventories;
      }
    },
    []
  );

  // Callback to accumulate selected inventories
  const handleSelectedInventories = useCallback((inventories: TInventory[]) => {
    setAllSelectedInventories(inventories);
  }, []);

  const toggleSelection = (id: string) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  return (
    <div>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Dispatch Printing
          </Title>
        </Col>
        <Col>
          <Radio.Group
            value={printMode}
            onChange={(e) => handlePrintModeChange(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="inventory">Print by Inventory</Radio.Button>
            <Radio.Button value="bookings">Print by Bookings</Radio.Button>
          </Radio.Group>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle" gutter={16}>
          <Col flex="auto">
            <Space wrap>
              {printMode === "bookings" && (
                <Space>
                  <CalendarOutlined style={{ color: "#666" }} />
                  <DatePicker
                    value={dayjs(selectedDate)}
                    onChange={handleDateChange}
                    format="YYYY-MM-DD"
                  />
                </Space>
              )}
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filters
                <DownOutlined />
              </Button>
            </Space>
          </Col>

          <Col>
            <Space align="center">
              <Text type="secondary">View:</Text>
              <Radio.Group
                value={compactView}
                onChange={handleCompactViewChange}
                buttonStyle="solid"
                size="small"
              >
                <Radio.Button value={false}>
                  <UnorderedListOutlined />
                </Radio.Button>
                <Radio.Button value={true}>
                  <AppstoreOutlined />
                </Radio.Button>
              </Radio.Group>
            </Space>
          </Col>
        </Row>

        {showFilters && (
          <>
            <Divider />
            <Row gutter={16}>
              {printMode === "bookings" && (
                <>
                  <Col xs={24} md={8}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Text strong>Vessel</Text>
                      <Select
                        style={{ width: "100%" }}
                        value={selectedVessel}
                        onChange={handleVesselChange}
                      >
                        <Option value="all">All Vessels</Option>
                        <Option value="SPRINTER-01">SPRINTER-01</Option>
                        <Option value="TRUCK-02">TRUCK-02</Option>
                        <Option value="VAN-01">VAN-01</Option>
                      </Select>
                    </Space>
                  </Col>
                  <Col xs={24} md={8}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Text strong>Client</Text>
                      <Select
                        style={{ width: "100%" }}
                        value={selectedClient}
                        onChange={handleClientChange}
                        loading={clientsLoading}
                      >
                        <Option value="all">All Clients</Option>
                        {clients?.items.map((client) => (
                          <Option key={client.id} value={client.id}>
                            {client.companyName || "Unnamed Client"}
                          </Option>
                        ))}
                      </Select>
                    </Space>
                  </Col>
                  <Col xs={24} md={8}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Text strong>Service Type</Text>
                      <Select style={{ width: "100%" }} defaultValue="all">
                        <Option value="all">All Types</Option>
                        <Option value="collection">Collection</Option>
                        <Option value="delivery">Delivery</Option>
                        <Option value="site-service">Site Service</Option>
                      </Select>
                    </Space>
                  </Col>
                </>
              )}
              {printMode === "inventory" && (
                <>
                  <Col xs={24} md={8}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Text strong>Client</Text>
                      <Select
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        value={selectedClient}
                        onChange={handleClientChange}
                        style={{ width: "100%" }}
                        placeholder="Select a client"
                        loading={clientsLoading}
                        options={[
                          { value: "all", label: "All clients" },
                          ...(clients?.items ?? []).map((client) => ({
                            value: client.id,
                            label: client.companyName || "Unnamed Client",
                          })),
                        ]}
                      />
                    </Space>
                  </Col>
                  <Col xs={24} md={8}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Text strong>Location</Text>
                      <Select
                        value={selectedLocation}
                        onChange={handleLocationChange}
                        style={{ width: "100%" }}
                        placeholder="Select a location"
                        loading={locationsLoading}
                      >
                        <Option value="all">All Locations</Option>
                        {locations?.items.map((location) => (
                          <Option key={location.id} value={location.id}>
                            {location.name}
                          </Option>
                        ))}
                      </Select>
                    </Space>
                  </Col>
                  <Col xs={24} md={8}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Text strong>Type</Text>
                      <Select
                        value={itemType}
                        onChange={handleItemTypeChange}
                        style={{ width: "100%" }}
                        placeholder="Select a type"
                      >
                        <Option value="all">All Types</Option>
                        <Option value={InventoryType.PACKAGE}>
                          Containers
                        </Option>
                        <Option value={InventoryType.PIECE}>Pieces</Option>
                      </Select>
                    </Space>
                  </Col>
                </>
              )}
            </Row>
          </>
        )}
      </Card>

      {/* Content */}
      {printMode === "inventory" && (
        <PrintByInventory
          selectedItems={selectedItems}
          toggleSelection={toggleSelection}
          selectedClient={selectedClient}
          setSelectedClient={(value) => setSelectedClient(value)}
          selectedLocation={selectedLocation}
          setSelectedLocation={(value) => setSelectedLocation(value)}
          compactView={compactView}
          setCompactView={(value) => setCompactView(value)}
          setSelectedItems={setSelectedItems}
          itemType={itemType}
          onSelectedInventories={handleSelectedInventories}
        />
      )}
      {printMode === "bookings" && (
        <PrintByBookings
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          selectedVessel={selectedVessel}
          setSelectedVessel={(value) => setSelectedVessel(value)}
          selectedClient={selectedClient}
          setSelectedClient={(value) => setSelectedClient(value)}
          compactView={compactView}
          setCompactView={(value) => setCompactView(value)}
          printOrder={printOrder}
        />
      )}

      {/* Print Options */}
      <Card>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              Print Options
            </Title>
          </Col>
          <Col>
            <Text type="secondary">
              {allSelectedInventories.length}{" "}
              {allSelectedInventories.length === 1 ? "item" : "items"} selected
            </Text>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>Copies per Label</Text>
              <InputNumber
                style={{ width: "100%" }}
                min={1}
                value={copyCount}
                onChange={(value) => setCopyCount(Math.max(1, value || 1))}
              />
            </Space>
          </Col>
          <Col xs={24} md={12}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>Print Order</Text>
              <Select
                style={{ width: "100%" }}
                value={printOrder}
                onChange={handlePrintOrderChange}
              >
                <Option value="default">Default Order (AHC Inventory #)</Option>
                <Option value="title">Sort by Title</Option>
                <Option value="client">Group by Client</Option>
                <Option value="location">Group by Location</Option>
                <Option value="type">Group by Type</Option>
              </Select>
            </Space>
          </Col>
        </Row>

        <Row justify="end">
          <PrintLabels
            selectedInventories={getSortedInventories(
              allSelectedInventories,
              printOrder
            )}
            copiesPerLabel={copyCount}
          />
        </Row>
      </Card>
    </div>
  );
};
